import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { StepIndicator, ValidateStep, PhoneStep, OtpStep, OtpMedium } from '@otp-session/shared';
import CardStep from '../components/CardStep/CardStep';
import ConfigDebug from '../components/ConfigDebug/ConfigDebug';
import { useCardViewRequest } from '../hooks/useCardViewRequest';
import './CardViewRequestPage.css';

const { version } = require('../../package.json');

type Step = 'validate' | 'phone' | 'otp' | 'card';

const CardViewRequestPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState<Step>('validate');
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);
  const [cvrt, setCvrt] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>('');

  const {
    phoneLastDigits,
    card,
    loading,
    error,
    errorStatus,
    senderName,
    validate,
    requestOtp,
    verifyOtpAndGetCard,
  } = useCardViewRequest();

  useEffect(() => {
    // Extract CVRT from URL path
    const pathParts = location.pathname.split('/').filter(part => part.length > 0);
    let cvrtFromUrl = '';
    
    // Handle different URL patterns
    if (pathParts.length === 1 && pathParts[0].length === 32) {
      // Direct CVRT: /01234567890123456789012345678901
      cvrtFromUrl = pathParts[0];
    } else if (pathParts.length === 2 && pathParts[0] === 'card-view-request') {
      // Structured path: /card-view-request/cvrt
      cvrtFromUrl = pathParts[1];
    }
    
    if (cvrtFromUrl && cvrtFromUrl.length === 32 && /^[a-zA-Z0-9]{32}$/.test(cvrtFromUrl)) {
      setCvrt(cvrtFromUrl);
    } else {
      // Invalid CVRT in URL
    }

    // Extract language from query params
    const urlParams = new URLSearchParams(location.search);
    const language = urlParams.get('language');
    if (language && ['en', 'es', 'fr'].includes(language)) {
      i18n.changeLanguage(language);
    }
  }, [location, i18n]);

  // Don't automatically clear errors - let them persist until user takes action
  // This ensures error messages are visible to the user

  const handleValidateSuccess = () => {
    setCompletedSteps(prev => [...prev, 'validate']);
    setCurrentStep('phone');
  };

  const handlePhoneSuccess = () => {
    setCompletedSteps(prev => [...prev, 'phone']);
    setCurrentStep('otp');
  };

  const handleOtpSuccess = () => {
    setCompletedSteps(prev => [...prev, 'otp']);
    setCurrentStep('card');
  };

  const handleCardSuccess = () => {
    setCompletedSteps(prev => [...prev, 'card']);
  };

  useEffect(() => {
    if (card) {
      handleCardSuccess();
    }
  }, [card]);

  const handleValidate = async (cvrtToken: string) => {
    const success = await validate(cvrtToken);
    if (success) {
      handleValidateSuccess();
    }
  };

  const handleRequestOtp = async (cvrtToken: string, phone: string, medium: OtpMedium): Promise<boolean> => {
    setPhoneNumber(phone); // Store phone number for resend
    const success = await requestOtp(cvrtToken, phone, medium);
    if (success) {
      handlePhoneSuccess();
    }
    return success;
  };

  const handleVerifyOtp = async (cvrtToken: string, otp: string): Promise<boolean> => {
    const success = await verifyOtpAndGetCard(cvrtToken, otp);
    if (success) {
      handleOtpSuccess();
    }
    return success;
  };

  const handleResendOtp = async (medium: OtpMedium): Promise<boolean> => {
    if (!phoneNumber) {
      return false;
    }
    const success = await requestOtp(cvrt, phoneNumber, medium);
    return success;
  };

  if (!cvrt) {
    return (
      <div className="card-view-request-page">
        <div className="error-container">
          <div className="alert alert-error">
            <h2>Invalid Request</h2>
            <p>Invalid or missing card view request token.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card-view-request-page">
      <div className="page-header">
        <h1 title={`v${version}`}>{t('title')}</h1>
      </div>

      <StepIndicator currentStep={currentStep} completedSteps={completedSteps} />

      <div className="step-content">
        {currentStep === 'validate' && (
          <ValidateStep
            sessionId={cvrt}
            phoneLastDigits={phoneLastDigits}
            loading={loading}
            error={error}
            errorStatus={errorStatus}
            onValidate={handleValidate}
            onNext={handleValidateSuccess}
          />
        )}

        {currentStep === 'phone' && (
          <PhoneStep
            sessionId={cvrt}
            phoneLastDigits={phoneLastDigits}
            loading={loading}
            error={error}
            senderName={senderName}
            onRequestOtp={handleRequestOtp}
            onNext={handlePhoneSuccess}
          />
        )}

        {currentStep === 'otp' && (
          <OtpStep
            sessionId={cvrt}
            loading={loading}
            error={error}
            onVerifyOtp={handleVerifyOtp}
            onNext={handleOtpSuccess}
            onResendOtp={handleResendOtp}
          />
        )}

        {currentStep === 'card' && (
          <CardStep
            card={card}
            loading={loading}
            error={error}
          />
        )}
      </div>
      
      <ConfigDebug />
    </div>
  );
};

export default CardViewRequestPage;